import React from 'react';
import ActivityMediumBox from '../Box/ActivityMediumBox';
import Buttonbar from '../Navbar/Buttonbar';
import ChipPlace from '../../../../components/Chip/ChipPlace';
import StackedBarChart from '../Chart/StackedBarChart';
import NoDataBox from '../Box/NoDataBox';
import { getHoverText, dayDate, yearDate } from '../../../../utils/summaryGraph';
import * as ActionColors from '../../../../themes/summaryReportColor';
import ViewSubTypeForYearRadioBar from '../Navbar/ViewSubTypeForYearRadioBar';

import * as moment from 'moment';

const TotalLocationLegend = (props) =>
  props.data.map((val, i) => {
    return (
      <div className='flex w-full my-auto' key={i}>
        <div className='block w-3/4'>
          <ChipPlace label={val.place} color={props.color[val.place]} />
        </div>
        <div className='block w-3/12'>
          <div className='text-xl font-medium text-right text-summary-header '>
            {val.value.toFixed(2) !== '0.00' ? val.value.toFixed(2) : '≈ 0'}
            <span className='px-2 text-base font-normal capitalize text-summary-header '>%</span>
          </div>
        </div>
      </div>
    );
  });

const Location = (props) => {
  const { t } = props;
  const margin = {
    top: 20, //20,
    right: 20, //20,
    bottom: 40, //40,
    left: 60, //60
  };
  const width = 740 - margin.left - margin.right;
  const height = 515 - margin.top - margin.bottom;

  const lableX = props.viewType === 'year' ? t('months') : t('days_for_graph');

  const viewSubTypeForYear = props.viewSubTypeForYear;
  const viewYearTypeRadioBar =
    props.viewType === 'year' ? (
      <ViewSubTypeForYearRadioBar
        viewType={props.viewType}
        viewSubType={viewSubTypeForYear}
        t={t}
        onChangeSubViewType={(subType) => {
          props.onChangeViewSubTypeForYear('location', subType);
        }}
      />
    ) : (
      <></>
    );

  const filteredLocationTotal = locationTotal(props.data);

  const colors = placeColors(filteredLocationTotal);

  const locationGraph = locationGraphData(
    props.date,
    props.data,
    props.viewType,
    viewSubTypeForYear,
    filteredLocationTotal,
  );

  return props.hasActivity ? (
    <div className='flex-wrap flex-auto min-w-0 px-16 mb-16'>
      <div className='relative flex-col flex-wrap block col-span-12 mx-3 my-4 bg-white rounded shadow-lg pb-9'>
        <div className='block col-span-12 px-1 pt-4 lg:flex md:flex xl:flex'>
          <Buttonbar onChangePeriod={props.onChangePeriod} viewType={props.viewType} t={t} />
          <div className='flex flex-auto justify-end  pr-8'>{viewYearTypeRadioBar}</div>
        </div>

        <div className='px-6 pb-5'>
          <div className='flex-wrap block xl:flex'>
            <div className='w-full p-4 pt-1 xl:w-2/3'>
              <div className='flex flex-wrap flex-grow col-span-12 my-1'></div>
              <div className='rounded-md xl:mb-5 lg:mb-5 bg-primary-gradient h-428'>
                <StackedBarChart
                  width={width}
                  height={height}
                  margin={margin}
                  color={colors}
                  rect={rectProperty(props.viewType, viewSubTypeForYear)}
                  data={locationGraph}
                  onMouseover={props.onMouseover}
                  onMouseout={props.onMouseout}
                  tooltip={props.tooltip}
                  tooltipName={props.tooltip.name}
                  unit={t('hours')}
                  lableX={lableX}
                />
              </div>
            </div>

            <div className='w-full xl:w-1/3 lg:mt-16 xl:mt-0 md:-mt-8'>
              <div className='lg:flex xl:block'>
                <div className='mx-4 my-2 xl:mx-4 lg:mx-2 md:mx-4'>
                  <ActivityMediumBox header={`${t('total')} / ${t(props.viewType)}`}>
                    <div className='mt-2'>
                      <TotalLocationLegend data={filteredLocationTotal} color={colors} />
                    </div>
                  </ActivityMediumBox>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className='flex-wrap flex-auto min-w-0 px-16'>
      <NoDataBox text={t('no_data')} />
    </div>
  );
};

const locationTotal = (data) => {
  const HIDE_VALUE_LESS_THAN = 0.01;
  const total = {};
  data.forEach((d) => {
    if (d.places.length > 0) {
      d.places.forEach((p) => {
        if (total.hasOwnProperty(p.place)) {
          total[p.place] += +p.durationInSeconds;
        } else {
          total[p.place] = +p.durationInSeconds;
        }
      });
    }
  });
  // calculate to hour
  for (const [key, value] of Object.entries(total)) {
    total[key] = value / 3600;
  }

  // sum all total
  const sumTotalTime = Object.values(total).reduce((a, b) => a + b, 0);

  const allTotalLocation = [];
  for (const [key, value] of Object.entries(total)) {
    allTotalLocation.push({
      place: key,
      value: value ? (value / sumTotalTime) * 100 : 0,
    });
  }

  const result = allTotalLocation
    .filter((r) => r.value > HIDE_VALUE_LESS_THAN)
    .sort((a, b) => (a.value < b.value ? 1 : b.value < a.value ? -1 : 0));

  const sumTotalResult = result.reduce((a, b) => {
    return a + b.value;
  }, 0);
  const other = allTotalLocation.filter((r) => r.value <= HIDE_VALUE_LESS_THAN);

  if (other.length > 0) {
    result.push({
      place: 'Others',
      value: 100 - sumTotalResult,
    });
  }
  return result;
};

const rectProperty = (viewType, viewSubTypeForYear) => {
  const rectWeek = { width: 30, padding: 0.7, maxValue: 24 };
  const rectMonth = { width: 15, padding: 0.5, maxValue: 24 };
  const rectYear = { width: 30, padding: 0.45, maxValue: 800 };
  const rectYearAverage = { width: 30, padding: 0.45, maxValue: 24 };

  if (viewType === 'week') {
    return rectWeek;
  } else if (viewType === 'month') {
    return rectMonth;
  }

  if (viewSubTypeForYear === 'average') {
    //for displaying the average per day (from the year graph)
    return rectYearAverage;
  }

  return rectYear;
};

const getLocationValue = (isCalucateAverageForYear, value, divider, noOfDate) => {
  if (isCalucateAverageForYear) {
    return (value / (noOfDate * divider)).toFixed(2);
  }
  return (value / divider).toFixed(2);
};

const locationGraphData = (date, dataOrig, viewType, viewSubTypeForYear, filteredLocation) => {
  const data = dataOrig
    .map((d) => {
      if (d.places.filter((p) => filteredLocation.find((f) => `${f.place}` === `${p.place}`)).length > 0) {
        const tmp = { ...d };
        tmp.places = tmp.places.filter((p) => filteredLocation.find((f) => `${f.place}` === `${p.place}`));
        return tmp;
      }
      return null;
    })
    .filter((d) => d);

  // const data = dataOrig.filter(d => {
  //   d.places = d.places.filter(p => filteredLocation.find(f => f.place === p.place));
  //   return d.places.length > 0;
  // });

  const templateLocationDate = {};
  for (const d of data || []) {
    d.places.forEach((p) => {
      templateLocationDate[p.place] = { value: 0, hoverTxt: '', nameTxt: '' };
    });
  }

  let templateData = [];
  let isCalucateAverageForYear = false;
  if (viewType === 'week') {
    const dateStart = moment(new Date(`${date}`))
      .subtract(1, 'w')
      .add(1, 'd')
      .startOf('day')
      .toDate();
    const dateEnd = moment(new Date(`${date}`))
      .endOf('day')
      .toDate();

    const dateRange = dayDate(dateStart, dateEnd, viewType);
    templateData = (dateRange || []).map((d) => {
      return { ...d, ...templateLocationDate };
    });
  } else if (viewType === 'month') {
    const dateStart = moment(new Date(`${date}`))
      .subtract(4, 'w')
      .add(1, 'd')
      .startOf('day')
      .toDate();
    const dateEnd = moment(new Date(`${date}`))
      .endOf('day')
      .toDate();

    const dateRange = dayDate(dateStart, dateEnd, viewType);
    templateData = (dateRange || []).map((d) => {
      return { ...d, ...templateLocationDate };
    });
  } else {
    if (viewSubTypeForYear === 'average') {
      isCalucateAverageForYear = true;
    }
    const dateStart = moment(new Date(`${date}`))
      .subtract(1, 'y')
      .add(1, 'd')
      .startOf('month')
      .toDate();
    const dateEnd = moment(new Date(`${date}`))
      .startOf('month')
      .toDate();

    const dateRange = yearDate(dateStart, dateEnd);
    templateData = (dateRange || []).map((d) => {
      return { ...d, ...templateLocationDate };
    });
  }
  (data || []).forEach((d) => {
    if (d.places.length > 0) {
      const refDate = moment(new Date(`${d.createdDate}`));
      const found = templateData.find((t) => {
        if (viewType === 'year') {
          return t.refDate === d.month;
        }
        return moment(t.refDate).isSame(refDate, 'day');
      });
      if (found) {
        d.places.forEach((p) => {
          found[p.place] = {
            value: +getLocationValue(isCalucateAverageForYear, p.durationInSeconds, 3600, d.noOfDate),
            hoverTxt: getHoverText(getLocationValue(isCalucateAverageForYear, p.durationInSeconds, 1, d.noOfDate)),
            nameTxt: p.place,
          };
        });
      }
    }
  });
  return templateData;
};

const placeColors = (data) => {
  const placeColors = {};

  (data || []).forEach((p, index) => {
    placeColors[p.place] = locationColors[index];
  });
  return placeColors;
};

const locationColors = ActionColors.COLOR_LOCATION;

export default Location;
